import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate } from '../../utils/formatters.js';

import { CycleCountModal } from './CycleCountModal.jsx';
import { CycleCountDetailModal } from './CycleCountDetailModal.jsx';

const STATUS_VARIANT = { OPEN: 'warning', POSTED: 'success', CANCELLED: 'neutral' };

export function CycleCountsPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const counts = useQuery({
    queryKey: ['inventory', 'cycle-counts'],
    queryFn: () => inventoryApi.listCycleCounts().then((r) => r.items),
  });
  const warehouses = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => inventoryApi.listWarehouses().then((r) => r.items),
  });

  const rows = counts.data || [];
  const whById = new Map((warehouses.data || []).map((w) => [w.id, w]));
  const open = rows.filter((r) => r.status === 'OPEN').length;
  const posted = rows.filter((r) => r.status === 'POSTED').length;

  const columns = [
    { key: 'countedAt', label: t('cyc.col.date'), render: (r) => formatDate(r.countedAt || r.createdAt) },
    { key: 'warehouse', label: t('cyc.col.warehouse'), render: (r) => whById.get(r.warehouseId)?.code || '-' },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{t(`cyc.status.${r.status}`)}</Badge> },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('cyc.action.view')}</Button>,
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('cyc.title')}</h1>
          <p className="muted">{t('cyc.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('cyc.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('cyc.metric.total')} value={String(rows.length)} />
        <MetricCard label={t('cyc.metric.open')} value={String(open)} trendVariant={open > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('cyc.metric.posted')} value={String(posted)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={counts.isLoading} emptyMessage={t('cyc.empty')} />

      <CycleCountModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <CycleCountDetailModal countId={detailId} onClose={() => setDetailId(null)} warehouse={detailId ? whById.get(rows.find((r) => r.id === detailId)?.warehouseId) : null} />
    </section>
  );
}
